import React from 'react';
import { Tag } from 'primereact/tag';
import { ToastService, useDataTable } from '@iamsoftware/react-hooks';
import { useNavigate } from 'react-router-dom';
import { AgreementService } from '../../../../../service/AgreementService';

interface CancelledTabProps {
    loadCanvas: (file: any) => void;
    handleShowFiles: (item: any) => void;
    headerResult?: any;
    setReload?: (reloadFn: () => void) => void;
}

export const CancelledTab: React.FC<CancelledTabProps> = ({
    loadCanvas,
    handleShowFiles,
    headerResult,
    setReload
}) => {
    const navigate = useNavigate();
    const columns: any[] = [
        {
            field: 'agreementName',
            header: 'Tên tài liệu',
            width: 100,
            matchMode: 'contains',
            dataType: 'custom' as const,
            customCell(row: any) {
                return (
                    <span 
                        className="underline text-primary cursor-pointer" 
                        onClick={() => navigate(`/quan-ly-tai-lieu/tai-lieu/${row.agreementId}?mode=view`)} 
                        title="Xem chi tiết"
                    >
                        {row.agreementName}
                    </span>
                );
            }
        },
        { field: 'agreementNo', header: 'Mã tài liệu', width: 140, matchMode: 'contains' },
        { field: 'agreementTypeName', header: 'Loại tài liệu', width: 160, matchMode: 'contains' },
        // {
        //     field: 'statusDescription',
        //     header: 'Trạng thái',
        //     width: 200,
        //     dataType: 'custom' as const,
        //     customCell(row: any) {
        //         return <Tag value="Đã hủy" severity="danger" />;
        //     }
        // },
        // {
        //     field: 'mainContent',
        //     header: 'File ký',
        //     width: 220,
        //     dataType: 'custom' as const,
        //     customCell(row: any) {
        //         if (!row.mainContent) return <span className="text-gray-500">Chưa có file</span>;
        //         return (
        //             <div className="flex align-items-center gap-2">
        //                 <i className={`pi ${row.mainContent.contentType === 'application/pdf' ? 'pi-file-pdf' : 'pi-file-word'} text-xl`}></i>
        //                 <div>
        //                     <div className="font-medium">{row.mainContent.fileName}</div>
        //                     <small className="text-gray-500">{row.mainContent.contentType?.split('/')?.[1]?.toUpperCase()}</small>
        //                 </div>
        //             </div>
        //         );
        //     }
        // },
        {
            field: 'contentCount',
            header: 'Số file',
            width: 110,
            dataType: 'custom' as const,
            customCell(rowData: any): any {
                const count = rowData.contentCount || 0;
                return (
                    <Tag
                        value={`${count} file`}
                        severity="info"
                        style={{ cursor: count > 0 ? 'pointer' : 'default' }}
                        onClick={() => count > 0 && handleShowFiles(rowData)}
                        title={count > 0 ? 'Click để xem danh sách file' : ''}
                    />
                );
            }
        },
        {
            field: 'expiryDate',
            header: 'Thời gian',
            width: 180,
            dataType: 'custom',
            customCell(rowData: any): any {
                const formatLocal = (value?: any) => {
                    if (!value) return null;
                    const d = new Date(value);
                    const dd = String(d.getDate()).padStart(2, '0');
                    const mm = String(d.getMonth() + 1).padStart(2, '0');
                    const yyyy = d.getFullYear();
                    const HH = String(d.getHours()).padStart(2, '0');
                    const MM = String(d.getMinutes()).padStart(2, '0');
                    const SS = String(d.getSeconds()).padStart(2, '0');
                    return `${dd}/${mm}/${yyyy} ${HH}:${MM}:${SS}`;
                };

                const expiryText = rowData.expiryDate ? formatLocal(rowData.expiryDate) : 'Không có thời hạn ký';
                const updatedText = rowData.lastUpdatedStamp ? formatLocal(rowData.lastUpdatedStamp) : null;

                return (
                    <div className="text-sm">
                        <div className="flex align-items-center gap-2 mb-1">
                            <i className="pi pi-hourglass" />
                            <span>{expiryText}</span>
                        </div>
                        {updatedText && (
                            <div className="flex align-items-center gap-2">
                                <i className="pi pi-clock" />
                                <span>{updatedText}</span>
                            </div>
                        )}
                    </div>
                );
            }
        }
    ];

    const mapListResult = (result: any) => {
        if (result.listData && Array.isArray(result.listData)) {
            result.listData = result.listData.map((item: any) => ({
                ...item,
                mainContent: item.mainFileName || item.mainContentType ? {
                    fileName: item.mainFileName,
                    contentType: item.mainContentType,
                    contentLocation: item.referenceContentLocation || item.mainContentLocation,
                    fileId: item.fileId || item.mainFileId || item.mainContentId,
                    googleFileId: item.fileId || item.mainFileId || item.mainContentId,
                    referenceFileId: item.referenceContentLocation
                } : null
            }));
        }
        const total = result.totalRecords ?? (result.listData || []).length;
        return {
            listData: result.listData || [],
            totalRecords: total,
            totalCount: total
        };
    };

    const { render: renderCancelled, reloadLazyData: reloadCancelled } = useDataTable({
        tableHeader: headerResult || (
            <div className="flex align-items-center justify-content-between flex-wrap gap-2">
                <span>Tài liệu đã hủy</span>
            </div>
        ),
        dataKey: 'agreementId',
        indexColumnWidth: 45,
        actionColumnWidth: 170,
        columns: columns,
        getList: lazyLoadEvent => {
            return AgreementService.getAgreementList(lazyLoadEvent, 'AGT_CANCELLED').then(mapListResult);
        },
        initActions(items: Array<any>) {
            items?.forEach(item => {
                const mainFileId = item?.mainContent?.fileId || item?.fileId;
                const hasMainFileUrl = item?.mainContent?.contentLocation;
                const actions: any[] = [];

                if (mainFileId || hasMainFileUrl) {
                    actions.push({
                        icon: 'pi pi-external-link',
                        title: 'Xem file',
                        className: 'p-button-help',
                        command: () => {
                            if (item.mainContent) {
                                loadCanvas(item.mainContent);
                            } else {
                                navigate(`/quan-ly-tai-lieu/tai-lieu/${item.agreementId}?mode=view`);
                            }
                        }
                    });
                } else {
                    actions.push({
                        icon: 'pi pi-eye',
                        title: 'Xem chi tiết',
                        className: 'p-button-info',
                        command: () => navigate(`/quan-ly-tai-lieu/tai-lieu/${item.agreementId}?mode=view`)
                    });
                }
                
                item.actions = actions;
            });
        },
        originalProps: { scrollHeight: 'calc(100vh - 300px)' }
    });

    React.useEffect(() => {
        if (setReload) setReload(reloadCancelled);
    }, [reloadCancelled, setReload]);

    return (
        <div className="tai-lieu-ky-table">
            {renderCancelled()}
        </div>
    );
};
